import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

type TokenGenerator = () => string | Promise<string>;

export class HttpClient {
  protected readonly http: AxiosInstance;
  private static instance?: HttpClient;
  private readonly tokenGenerator?: TokenGenerator;

  private constructor(tokenGenerator: TokenGenerator) {
    this.http = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL });
    this.tokenGenerator = tokenGenerator;
    this.http.interceptors.request.use(
      async (config) => {
        const token = await this.getToken();
        config.headers.set('Authorization', `Bearer ${token}`);
        return config;
      },
      (error) => {
        Promise.reject(error);
      },
    );
  }

  request<T = any, R = AxiosResponse<T, any>, D = any>(config: AxiosRequestConfig<D>): Promise<R> {
    return this.http.request(config);
  }

  get<T = any, R = AxiosResponse<T, any>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.get(url, config);
  }

  delete<T = any, R = AxiosResponse<T, any>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.delete(url, config);
  }

  head<T = any, R = AxiosResponse<T, any>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.head(url, config);
  }

  options<T = any, R = AxiosResponse<T, any>, D = any>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.options(url, config);
  }

  post<T = any, R = AxiosResponse<T, any>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.post(url, data, config);
  }

  put<T = any, R = AxiosResponse<T, any>, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> {
    return this.http.put(url, data, config);
  }

  patch<T = any, R = AxiosResponse<T, any>, D = any>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>,
  ): Promise<R> {
    return this.http.patch(url, data, config);
  }

  static getInstance() {
    if (this.instance === undefined) {
      throw new Error('Instance not initialized, please use createInstance method.');
    }
    return this.instance;
  }

  static createInstance(tokenGenerator: TokenGenerator) {
    if (this.instance === undefined) {
      this.instance = new HttpClient(tokenGenerator);
    }
    return this.instance;
  }

  getToken() {
    return this.tokenGenerator && this.tokenGenerator();
  }
}

export abstract class ApiClient {
  private static _http: HttpClient | null = null;

  protected static get http() {
    if (!this._http) {
      this._http = HttpClient.getInstance();
    }
    return this._http;
  }
}
